import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import SafeDeferredRender, { RenderMode } from 'app/shared/modules/SafeDeferredRender';

import UserItemActions from 'app/shared/flux/actions/UserItemActions';
import AppActions from 'app/shared/flux/actions/AppActions';
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';

import FavoriteButton from 'app/shared/modules/FavoriteButton';
import LazyLoader from '../../LazyLoader';
import Carousel from './Carousel';
import TotalMonthlyPriceBadge from 'app/shared/modules/TotalMonthlyPrice/TotalMonthlyPriceBadge';
import NewUnitBadge from 'app/shared/modules/hdp/NewUnitBadge';
import PriceDropBadge from 'app/shared/modules/hdp/PriceDropBadge';
import ListingCardContactButton from './ListingCardContactButton';
import ListingCardCallButton from './ListingCardCallButton';

import Icon3DGlasses from 'images/icons/3d-glasses.svg';
import IconVerified from 'images/verified.svg';
import SkeletonCard from './skeleton';
import noPhoto from 'images/noPhoto.png';

import {
  listingUtils_checkFavorite,
  listingUtils_formatPropertyType,
  listingUtils_isMultifamilyType,
} from 'app/shared/utils/listingUtils';
import { shouldShowTotalMonthlyPriceBadge } from 'app/shared/utils/totalMonthlyPriceUtils';
import stringHelper from 'app/shared/utils/stringHelper';
import RouteActions from 'app/shared/flux/actions/RouteActions';
import { useYieldCallback } from '@zillow/yield-callback';
import experimentConstants from 'app/shared/constants/ExperimentConstants';

import perfUtils from 'app/shared/utils/perfUtils';
import {
  badgeContainerCX,
  badgeOutlineCX,
  buttonContainerCX,
  detailedInfoTextCX,
  getDetailedInfoContainerCX,
  getHeroPhotoContainerCX,
  getInfoBadgeCX,
  getListingCardContainerCX,
  infoBadgesCX,
  listingCardAnchorCX,
  priceTextCX,
} from './styles';
import { useListing } from '../ListingCardProvider';
import {
  emitTrackSaveHomeClickEvent,
  emitTrackUnSaveHomeClickEvent,
} from 'app/shared/models/Clickstream/HdpClickstreamEvents';
import type { RootReduxState } from 'app/shared/flux/reducers';
import { noop } from 'lodash';
import { useLocation } from 'react-router';
import routeUtils from 'app/shared/utils/routeUtils';

// Safe render utility to prevent object rendering errors
const safeRender = (value: unknown) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (React.isValidElement(value)) return value;

  // If it's an object, return empty string to prevent crash
  return '';
};

const ListingCardV3 = ({
  isCarouselCard = false,
  listingViewed = false,
  keepMapLocation = false,
  onClick = noop,
  onContactClick = noop,
  shouldLazyLoadListingCardPhotos = false,
  shouldDeferRender = false,
  isApplyInstantlyExperiment = false,
  highPriority = false,
  hideContactButton = false,
  forceUniformHeight = false,
}) => {
  const { listing, triggerLocation, triggerObject } = useListing();
  const dispatch = useDispatch();
  const location = useLocation();
  const screenWidth = useSelector((state: RootReduxState) => state.app.device.screenWidth);
  const filter = useSelector((state: RootReduxState) => state.filter);
  const newUnitsExperiment = useSelector(
    (state: RootReduxState) => state?.analytics?.runningExperiments?.[experimentConstants.HOTPADS_WEB_NEW_UNITS] || {},
  );
  const isNewUnitsControl = newUnitsExperiment?.isControl !== false;
  const priceDropExperiment = useSelector(
    (state: RootReduxState) =>
      state?.analytics?.runningExperiments?.[experimentConstants.HOTPADS_WEB_PRICE_DROPS] || {},
  );
  const isPriceDropControl = priceDropExperiment?.isControl !== false;
  const {
    active,
    address,
    displayName,
    has3dTour,
    hasSpecialOffers,
    incomeRestricted,
    listingMinMaxPriceBeds,
    medPhotoUrls,
    newUnitCount,
    priceDropCount,
    previewPhotoMed,
    recencyTime,
    rentalApplicationStatus,
    modelTypeUnitCount,
    seniorHousing,
    studentHousing,
    trusted,
    propertyType,
  } = listing;
  const formattedPropertyType = listingUtils_formatPropertyType(propertyType);
  const isFavorited = listing ? listingUtils_checkFavorite(listing) : false;

  const pathname = location.pathname;
  const isAreaPage = routeUtils.isAreaUrl(pathname);

  // Determine if card width will be < 400px
  // Desktop: carousel cards only (286px/300px)
  // Mobile: both SRP cards and carousel cards (both < 400px when screenWidth is 'sm' or 'xs')
  const isMobile = screenWidth === 'sm' || screenWidth === 'xs';
  const shouldUseNewLayout =
    (shouldShowTotalMonthlyPriceBadge(listing) && (isCarouselCard || isMobile)) || forceUniformHeight;

  // Determine if contact button should be shown
  // Contact button is shown when ALL of the following conditions are met:
  // - isCarouselCard: NOT a carousel card (e.g., Listing Cards in a carousel / see: HomeHub)
  // - listing.active: Listing is active (inactive listings can't be contacted)
  // - isAreaPage: User is on SRP page (not HDP or other page types)
  // - hideContactButton: allows parent components to override and hide the button, such as ContactSimilarListingsV2 on SRP
  const shouldShowContactButton =
    !isCarouselCard && listing && listing.active !== false && isAreaPage && !hideContactButton;

  // Determine if call button should be shown (only for multifamily listings)
  const isMultifamily = listingUtils_isMultifamilyType(propertyType);
  const shouldShowCallButton = shouldShowContactButton && isMultifamily;

  const yieldFavoriteClick = useYieldCallback(() => {
    if (!isFavorited) {
      emitTrackSaveHomeClickEvent({ listing, triggerLocation, triggerObject });
    } else {
      emitTrackUnSaveHomeClickEvent({ listing, triggerLocation, triggerObject });
    }

    dispatch(UserItemActions.toggleUserItem('favorite', listing));
  });

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    yieldFavoriteClick();
  };

  const yieldListingClickDispatches = useYieldCallback((isTransitioning = true) => {
    onClick();
    perfUtils.setMarker('ListingCardV3Click');
    dispatch(AppActions.setAreaPageScrollPosition(listing.maloneLotIdEncoded));
    dispatch(ListingEngineActions.setPreviewListing(listing));
    dispatch(ListingEngineActions.setActiveMarkerMaloneLotId(listing.maloneLotIdEncoded));
    if (listing?.uriV2 && isTransitioning) {
      dispatch(RouteActions.transitionToListing({ listingUri: listing.uriV2, keepMapLocation, filter }));
    }
  });

  const handleListingClick = useCallback(
    (e: React.MouseEvent, isTransitioning = true) => {
      if (e) {
        // Check if click originated from tooltip trigger or tooltip content
        const target = e.target as HTMLElement;
        const clickedTooltipTrigger = target.closest('[data-testid="listing-price-tooltip"]');
        const clickedTooltipContent = target.closest('[role="tooltip"]');
        const clickedTotalPriceBadge = target.closest('[data-testid="listing-price-tooltip"]');

        if (clickedTooltipTrigger || clickedTooltipContent || clickedTotalPriceBadge) {
          e.preventDefault();
          e.stopPropagation();
          return; // Don't navigate to listing
        }

        e.preventDefault();
        e.stopPropagation();
      }

      yieldListingClickDispatches(isTransitioning);
    },
    [yieldListingClickDispatches],
  );

  const handleCardKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
        e.preventDefault();
        yieldListingClickDispatches(true);
      }
    },
    [yieldListingClickDispatches],
  );

  if (!listing) {
    return null;
  }

  if (previewPhotoMed?.url && medPhotoUrls.length === 0) {
    medPhotoUrls.push(previewPhotoMed.url);
  }

  const isBrokerListing = listing?.broker?.isBrokerExclusiveListing || false;
  const brokerName = listing?.broker?.companyName || '';
  const displayBrokerInfo = isBrokerListing && brokerName;

  const FavoriteButtonComponent = (
    <div style={{ position: 'absolute', top: '5px', right: '0px', zIndex: '1' }}>
      <FavoriteButton
        alreadyFavorited={isFavorited}
        isBiggerListingCard
        onClick={handleFavoriteClick}
        listing={listing}
        size="36px"
      />
    </div>
  );

  const badges = [];

  // Safe function to get display diff as string
  const getDisplayDiff = () => {
    try {
      if (!recencyTime?.displayDiff) return '';
      const value = recencyTime.displayDiff;
      if (typeof value === 'string') return value;
      if (typeof value === 'number') return String(value);
      if (typeof value === 'boolean') return String(value);
      // If it's an object, handle it safely
      if (typeof value === 'object') {
        if (value === null) return '';
        // If it has a toString method, use it
        if (typeof value.toString === 'function' && value.toString !== Object.prototype.toString) {
          return value.toString();
        }
        // Fallback to JSON representation but avoid circular references
        return JSON.stringify(value);
      }
      // Last resort fallback
      return String(value || '');
    } catch (e) {
      return '';
    }
  };

  if (active && recencyTime?.daysDiff < 5) {
    const displayDiff = getDisplayDiff();

    badges.push(
      <div key="new" className={getInfoBadgeCX()} data-name="InfoBadge">
        New {safeRender(displayDiff)} ago
      </div>,
    );
  }

  if (hasSpecialOffers) {
    badges.push(
      <div key="special-offer" className={getInfoBadgeCX('upsell')} data-name="InfoBadge" aria-label="special offer">
        Special offer
      </div>,
    );
  }

  // Priority logic: Price drop > New unit (never show both)
  const showPriceDropBadge = priceDropCount > 0 && !isPriceDropControl;
  const showNewUnitBadge = !showPriceDropBadge && newUnitCount > 0 && !isNewUnitsControl;

  if (showPriceDropBadge) {
    badges.push(<PriceDropBadge key="price-drops" text="Price drop" variant="srp" showTooltip={false} />);
  }

  if (showNewUnitBadge) {
    const unitText = newUnitCount === 1 ? 'new unit' : 'new units';
    badges.push(
      <NewUnitBadge key="new-units" text={`${newUnitCount} ${unitText}`} variant="srp" showTooltip={false} />,
    );
  }

  if (rentalApplicationStatus === 'accepted') {
    badges.push(
      <div
        key="applications"
        className={getInfoBadgeCX('upsell')}
        data-name="InfoBadge"
        aria-label="accepts applications"
      >
        {isApplyInstantlyExperiment ? 'Apply instantly' : 'Accepts applications'}
      </div>,
    );
  }

  if (incomeRestricted) {
    badges.push(
      <div
        key="income-restricted"
        className={getInfoBadgeCX('housing')}
        data-name="InfoBadge"
        aria-label="income restricted"
      >
        Income restricted
      </div>,
    );
  }

  if (studentHousing) {
    badges.push(
      <div
        key="student-housing"
        className={getInfoBadgeCX('housing')}
        data-name="InfoBadge"
        aria-label="student housing"
      >
        Student housing
      </div>,
    );
  }

  if (seniorHousing) {
    badges.push(
      <div key="senior-housing" className={getInfoBadgeCX('housing')} data-name="InfoBadge" aria-label="senior housing">
        Senior housing
      </div>,
    );
  }

  // Limit to 2 badges if isCarouselCard is true
  const displayBadges = isCarouselCard ? badges.slice(0, 2) : badges;

  const PhotosComponent = (
    <figure className={getHeroPhotoContainerCX({ isCarouselCard })} data-name="HeroPhotoContainer">
      {FavoriteButtonComponent}
      {medPhotoUrls?.length > 0 && (
        <Carousel
          displayName={displayName}
          handleListingClick={handleListingClick}
          photoUrls={medPhotoUrls}
          isCarouselCard={isCarouselCard}
          highPriority={highPriority}
          shouldLazyLoadListingCardPhotos={shouldLazyLoadListingCardPhotos}
        />
      )}
      {medPhotoUrls?.length === 0 && (
        <img
          style={{ height: '100%' }}
          src={noPhoto}
          onClick={handleListingClick}
          alt=""
          {...(isCarouselCard && { width: 298, height: 224 })}
        />
      )}
      <div className={badgeContainerCX} data-name="BadgeContainer" onClick={handleListingClick}>
        {displayBadges.length > 0 && (
          <React.Fragment>
            {displayBadges.filter(Boolean).map((badge, index) => {
              if (React.isValidElement(badge)) {
                return React.cloneElement(badge, { key: badge.key || index });
              }
              return null;
            })}
          </React.Fragment>
        )}
      </div>
    </figure>
  );

  return (
    <article
      className={getListingCardContainerCX({
        listingViewed,
        isCarouselCard,
        shouldUseNewLayout,
        shouldShowContactButton,
      })}
      data-name="ListingCardContainer"
      tabIndex={0}
      onKeyDown={handleCardKeyDown}
      role="link"
      aria-label={`${displayName} - ${listingMinMaxPriceBeds?.priceDisplayRange || ''}`}
    >
      <SafeDeferredRender
        mode={
          shouldDeferRender
            ? RenderMode.RENDER_SERVER_SIDE_HYDRATE_ON_VISIBLE
            : RenderMode.RENDER_SERVER_SIDE_HYDRATE_ON_IMMEDIATE
        }
        visibilityOptions={{ rootMargin: '150px' }}
      >
        {shouldLazyLoadListingCardPhotos ? (
          <LazyLoader
            height="auto"
            width="auto"
            customPlaceholder={<figure className={getHeroPhotoContainerCX()} data-name="HeroPhotoContainerLazyLoad" />}
          >
            {PhotosComponent}
          </LazyLoader>
        ) : (
          PhotosComponent
        )}
        <section
          className={getDetailedInfoContainerCX({ shouldUseNewLayout })}
          data-name="DetailedInfoContainer"
          onClick={handleListingClick}
        >
          <div className={infoBadgesCX} data-name="InfoBadges">
            {trusted && (
              <div className={badgeOutlineCX} data-name="BadgeOutline">
                <img
                  src={IconVerified}
                  height="14px"
                  width="14px"
                  alt="This is a verified listing"
                  title="This is a verified listing"
                />
              </div>
            )}
            {has3dTour && (
              <div className={badgeOutlineCX} data-name="BadgeOutline">
                <img
                  src={Icon3DGlasses}
                  height="12px"
                  width="16px"
                  alt="This listing has a 3D tour available"
                  title="This listing has a 3D tour available"
                />
              </div>
            )}
          </div>
          <div className={detailedInfoTextCX} data-name="DetailedInfoText" onClick={handleListingClick}>
            {(() => {
              // Use the shouldUseNewLayout variable calculated earlier (based on card width < 400px)

              if (shouldUseNewLayout) {
                // 5-line layout for cards < 400px: Price -> Badge -> Beds/Baths -> Name -> Location
                return (
                  <>
                    <div
                      className={priceTextCX}
                      data-name="PriceText"
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      {listingMinMaxPriceBeds.priceDisplayRange}
                    </div>
                    <div>
                      <TotalMonthlyPriceBadge
                        listing={listing}
                        iconVariant="default"
                        size="sm"
                        data-testid="listing-price-tooltip"
                      />
                    </div>
                    <p style={{ fontWeight: 'bold' }}>
                      {safeRender(listingMinMaxPriceBeds?.bedDisplaySemiCompact)}
                      {listingMinMaxPriceBeds?.bathDisplaySemiCompact && (
                        <span>, {safeRender(listingMinMaxPriceBeds.bathDisplaySemiCompact)}</span>
                      )}
                      {modelTypeUnitCount
                        ? ` | ${stringHelper.pluralize(modelTypeUnitCount, 'unit', 'units')} available`
                        : ''}
                    </p>
                    <a
                      aria-label={`See more details about ${address?.hideAddress ? 'this listing' : displayName}`}
                      className={listingCardAnchorCX}
                      data-name="ListingCardAnchor"
                      href={listing.uriV2}
                      tabIndex={-1}
                      onClick={() => {
                        handleListingClick(null as unknown as React.MouseEvent, false);
                      }}
                    >
                      <h3 style={{ display: 'inline' }}>{safeRender(displayName)}</h3>
                    </a>
                    <span>{displayBrokerInfo ? ` | ${brokerName || ''}` : ''}</span>
                    <p>{`${formattedPropertyType || ''} in ${address?.city ? `${address.city}, ${address.state}` : ''}`}</p>
                  </>
                );
              } else {
                // Original 4-line layout for cards >= 400px: Price+Badge -> Beds/Baths -> Name -> Location
                return (
                  <>
                    <div
                      className={priceTextCX}
                      data-name="PriceText"
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      {listingMinMaxPriceBeds.priceDisplayRange}
                      <TotalMonthlyPriceBadge
                        listing={listing}
                        iconVariant="default"
                        size="sm"
                        data-testid="listing-price-tooltip"
                      />
                    </div>
                    <p style={{ fontWeight: 'bold' }}>
                      {safeRender(listingMinMaxPriceBeds?.bedDisplaySemiCompact)}
                      {listingMinMaxPriceBeds?.bathDisplaySemiCompact && (
                        <span>, {safeRender(listingMinMaxPriceBeds.bathDisplaySemiCompact)}</span>
                      )}
                      {modelTypeUnitCount
                        ? ` | ${stringHelper.pluralize(modelTypeUnitCount, 'unit', 'units')} available`
                        : ''}
                    </p>
                    <a
                      aria-label={`See more details about ${address?.hideAddress ? 'this listing' : displayName}`}
                      className={listingCardAnchorCX}
                      data-name="ListingCardAnchor"
                      href={listing.uriV2}
                      tabIndex={-1}
                      onClick={() => {
                        handleListingClick(null as unknown as React.MouseEvent, false);
                      }}
                    >
                      <h3 style={{ display: 'inline' }}>{safeRender(displayName)}</h3>
                    </a>
                    <span>{displayBrokerInfo ? ` | ${brokerName || ''}` : ''}</span>
                    <p>{`${formattedPropertyType || ''} in ${address?.city ? `${address.city}, ${address.state}` : ''}`}</p>
                  </>
                );
              }
            })()}
          </div>
        </section>
        {shouldShowContactButton && (
          <div className={buttonContainerCX}>
            {shouldShowCallButton && <ListingCardCallButton listing={listing} onClick={onContactClick} />}
            <ListingCardContactButton listing={listing} onClick={onContactClick} isInButtonContainer />
          </div>
        )}
      </SafeDeferredRender>
    </article>
  );
};

ListingCardV3.propTypes = {
  isCarouselCard: PropTypes.bool,
  listingViewed: PropTypes.bool,
  keepMapLocation: PropTypes.bool,
  onClick: PropTypes.func,
  onContactClick: PropTypes.func,
  shouldLazyLoadListingCardPhotos: PropTypes.bool,
  shouldDeferRender: PropTypes.bool,
  isApplyInstantlyExperiment: PropTypes.bool,
  highPriority: PropTypes.bool,
  hideContactButton: PropTypes.bool,
  forceUniformHeight: PropTypes.bool,
};

export { SkeletonCard };
export default ListingCardV3;
